/**
 * GlassConfirmSheet
 * Glassmorphic confirm dialog — same position:absolute overlay pattern as GlassPicker.
 * Render it inside the parent IonModal so it shares the stacking context (works on iOS).
 */
import React, { useState } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { warningOutline } from 'ionicons/icons';
import styles from './GlassConfirmSheet.module.scss';

interface Props {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning';
  onConfirm: () => void | Promise<void>;
  onDismiss: () => void;
}

const GlassConfirmSheet: React.FC<Props> = ({
  isOpen, title, message,
  confirmLabel = 'Delete', cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm, onDismiss,
}) => {
  const [busy, setBusy] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
      onDismiss();
    }
  };

  return (
    <div className={styles.backdrop} onClick={onDismiss}>
      <div className={styles.card} onClick={e => e.stopPropagation()}>

        <div className={`${styles.iconWrap} ${variant === 'danger' ? styles.iconDanger : styles.iconWarning}`}>
          <IonIcon icon={warningOutline} className={styles.icon} />
        </div>

        <h2 className={styles.title}>{title}</h2>
        <p className={styles.message}>{message}</p>

        <div className={styles.btns}>
          <button type="button" className={styles.cancelBtn} disabled={busy} onClick={onDismiss}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`${styles.confirmBtn} ${variant === 'danger' ? styles.confirmDanger : styles.confirmWarning}`}
            disabled={busy}
            onClick={handleConfirm}
          >
            {busy ? <IonSpinner name="bubbles" color="primary" className={styles.spinner} /> : confirmLabel}
          </button>
        </div>

      </div>
    </div>
  );
};

export default GlassConfirmSheet;
